import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { AlertController, NavController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, lastValueFrom, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GetPublicToken, ResetAllTokens } from 'src/app/store';
import { AuthState } from 'src/app/store/auth/auth.state';
import { environment } from 'src/environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    reload = false;
    constructor(
        public store: Store,
        private toastController: ToastController,
        private translateService: TranslateService,
        private alertController: AlertController,
        private navCtrl: NavController
    ) {}
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.store.selectSnapshot(AuthState.token);
        const publicToken = this.store.selectSnapshot(AuthState.publicToken);

        if (token) {
            request = request.clone({
                setHeaders: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    Authorization: `Bearer ${token}`,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Force-Update-Version': 'V2',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Public-Token': publicToken
                }
            });
        } else if (publicToken) {
            request = request.clone({
                setHeaders: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    Authorization: `Bearer ${publicToken}`,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Force-Update-Version': 'V2'
                }
            });
        }

        return next.handle(request).pipe(
            tap((httpEvent: HttpEvent<any>) => {
                if (httpEvent instanceof HttpResponse) {
                    if (httpEvent.headers.has('Should-Update-Version') && httpEvent.headers.get('Should-Update-Version') === 'true') {
                        try {
                            if (new URL(request.url)) {
                                if (new URL(request.url).hostname === new URL(environment.apiUrl).hostname) {
                                    if (!this.reload) {
                                        this.reloadApplication();
                                        this.reload = true;
                                    }
                                }
                            }
                        } catch (error) {}
                    }
                }
            }),
            catchError((error: HttpErrorResponse) => {
                if (request.headers.get('Skip-Error-Handling')) {
                    return throwError(error);
                }
                if (error?.error?.statusCode === 422 && error?.error?.message) {
                    this.presentToast(error?.error?.message);
                }

                return throwError(error);
            })
        );
    }

    private async reloadApplication() {
        const alert = await this.alertController.create({
            header: this.translateService.instant('Sesiune expirată'),
            message: this.translateService.instant('Sesiunea dvs. de utilizator a expirat'),
            buttons: [
                {
                    text: this.translateService.instant('OK'),
                    role: 'confirm'
                }
            ]
        });

        await alert.present();

        await alert.onDidDismiss();
        await lastValueFrom(this.store.dispatch(new ResetAllTokens()));
        await this.getPublicToken();

        this.navCtrl.navigateRoot('account').then(() => {
            setTimeout(() => {
                this.navCtrl.navigateForward('account/login');
                this.reload = false;
            }, 600);
        });
    }

    private async presentToast(message: string) {
        const toast = await this.toastController.create({
            message,
            duration: 4000,
            position: 'top',
            color: 'primary',
            cssClass: 'warning-message'
        });

        await toast.present();
    }

    private async getPublicToken() {
        const deviceId = (await Device.getId()).identifier;
        await lastValueFrom(this.store.dispatch(new GetPublicToken(deviceId)));
    }
}
